import { Link } from "react-router-dom";
import { usePopup } from "@/lib/PopupContext";
import { openChat } from "@/lib/chat";
import { SERVICE_MENU, FAQ_DATA, WHY_CHOOSE } from "@/lib/constants";
import PricingSection from "@/components/shared/PricingSection";
import TestimonialSlider from "@/components/shared/TestimonialSlider";
import ProcessSection from "@/components/shared/ProcessSection";
import FAQSection from "@/components/shared/FAQSection";
import CTABanner from "@/components/shared/CTABanner";
import { useMeta } from "@/hooks/useMeta";
import {
  ArrowRight,
  Shield,
  FileCheck,
  Scale,
  Building2,
  Gavel,
  Briefcase,
} from "lucide-react";

const CATEGORY_ICONS = [Shield, FileCheck, Scale, Building2, Gavel, Briefcase];

export default function Home() {
  const { openPopup } = usePopup();

  useMeta({
    title:
      "AmericanTrademark Firm | Trademark Registration & IP Protection Services in the USA",
    description:
      "Protect your brand with professional trademark registration, copyright protection, patent filing, and corporate services. Trusted by businesses across the USA.",
    keywords:
      "trademark registration, trademark filing, USPTO, brand protection, copyright registration, patent filing, company formation, intellectual property, USA",
  });

  return (
    <div data-testid="home-page">
      {/* 1. Hero Section */}
      <section
        className="relative py-24 md:py-36 overflow-hidden"
        data-testid="hero-section"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1587162146766-e06b1189b907?w=1920&q=80)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#09090B]/80 via-[#09090B]/65 to-[#09090B]/40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#6B9FFF] mb-4 animate-fade-in">
              Trusted Trademark Services in the USA
            </p>
            <h1 className="font-heading font-black text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-none text-white mb-6 animate-slide-in-up">
              Protect Your Brand.
              <br />
              <span className="text-[#6B9FFF]">Secure Your Future.</span>
            </h1>
            <p className="text-lg md:text-xl text-zinc-300 font-body leading-relaxed mb-10 max-w-xl animate-slide-in-up stagger-2">
              Comprehensive trademark registration, copyright protection, and
              corporate services. We handle the legal complexities so you can
              focus on growing your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-slide-in-up stagger-3">
              <button
                onClick={() => openPopup()}
                className="bg-[#002FA7] text-white hover:bg-[#002480] transition-colors px-8 py-4 font-bold text-sm tracking-widest uppercase"
                data-testid="hero-register-btn"
              >
                Register My Trademark
              </button>
              <button
                // onClick={openChat}
                className="js-chat bg-transparent text-white hover:bg-white/10 transition-colors px-8 py-4 font-bold text-sm tracking-widest uppercase border border-white text-center"
                data-testid="hero-chat-btn"
              >
                Chat Now
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Info Section with Image */}
      <section
        className="py-24 md:py-32 bg-[#F4F4F5]"
        data-testid="info-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1672380135241-c024f7fbfa13?w=800&q=80"
                alt="Professional business handshake"
                className="w-full h-[400px] object-cover"
                loading="lazy"
              />
            </div>
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-3">
                About Our Services
              </p>
              <h2 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight text-[#09090B] leading-tight mb-6">
                Your Brand Deserves Legal Protection
              </h2>
              <p className="text-base md:text-lg text-zinc-600 font-body leading-relaxed mb-4">
                In today's competitive marketplace, your brand identity is one
                of your most valuable assets. Without proper legal protection,
                your business name, logo, and reputation are vulnerable to
                infringement and misuse.
              </p>
              <p className="text-base text-zinc-600 font-body leading-relaxed mb-6">
                AmericanTrademark Firm provides end-to-end intellectual property
                services designed for businesses of all sizes. From trademark
                registration to patent filing, our team of legal experts ensures
                your creative and commercial assets are fully protected under
                U.S. law.
              </p>
              <button
                onClick={() => openPopup()}
                className="bg-[#002FA7] text-white hover:bg-[#002480] transition-colors px-8 py-4 font-bold text-sm tracking-widest uppercase"
                data-testid="info-cta-btn"
              >
                Get Started Today
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Services Overview */}
      <section
        className="py-24 md:py-32 bg-white"
        data-testid="services-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-3">
              Our Services
            </p>
            <h2 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight text-[#09090B] leading-tight">
              Comprehensive Legal Solutions
            </h2>
          </div>
          <div className="mobile-slider md:grid-cols-2 lg:grid-cols-3 gap-0">
            {SERVICE_MENU.map((cat, idx) => {
              const Icon = CATEGORY_ICONS[idx] || Shield;
              return (
                <Link
                  key={idx}
                  to={`/services/${cat.services[0].slug}`}
                  className="service-card border border-[#E4E4E7] p-8 group block"
                  data-testid={`service-card-${idx}`}
                >
                  <Icon className="w-8 h-8 text-[#002FA7] mb-4" />
                  <h3 className="font-heading font-bold text-xl text-[#09090B] mb-3">
                    {cat.category}
                  </h3>
                  <p className="text-sm text-zinc-600 font-body mb-4">
                    {cat.services.length} specialized services to support your
                    business needs.
                  </p>
                  <span className="inline-flex items-center gap-1 text-sm font-bold text-[#002FA7] group-hover:gap-2 transition-all">
                    Explore <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* 4. Pricing Section */}
      <PricingSection />

      {/* 5. Process Section */}
      <ProcessSection />

      {/* 6. Testimonials */}
      <TestimonialSlider />

      {/* 7. Why Choose Us */}
      <section
        className="py-24 md:py-32 bg-white"
        data-testid="why-choose-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-3">
              Why Choose Us
            </p>
            <h2 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight text-[#09090B] leading-tight">
              The AmericanTrademark Firm Advantage
            </h2>
          </div>
          <div className="mobile-slider md:grid-cols-2 lg:grid-cols-3 gap-0">
            {WHY_CHOOSE.map((item, idx) => (
              <div
                key={idx}
                className="border border-[#E4E4E7] p-8"
                data-testid={`why-choose-${idx}`}
              >
                <span className="font-heading font-black text-4xl text-[#002FA7] opacity-20 block mb-2">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <h3 className="font-heading font-bold text-lg text-[#09090B] mb-3">
                  {item.title}
                </h3>
                <p className="text-sm text-zinc-600 font-body leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. FAQ */}
      <FAQSection items={FAQ_DATA} />

      {/* 9. CTA Banner */}
      <CTABanner />
    </div>
  );
}
